import {Action,MatchPhase,attackDef,type ArenaDefinition,type ArenaState,type CombatEvent,type PlayerState} from '@arena/shared';
import {kitOf,summonOf,LOVECRAFT_TENTACLE} from '@arena/character-data';
import {SoundMixer,type SoundPoint} from './mixer.ts';
import {creatureSound,moveSound,zoneSound} from './profiles.ts';

type Pose=Pick<PlayerState,'x'|'y'|'z'|'grounded'|'vy'|'dashTicks'|'knockedOut'|'matchFrozen'|'action'|'actionTick'|'actionSerial'>&{spinTicks?:number};
type Track={x:number;y:number;z:number;grounded:boolean;dash:number;steps:number;seen:Map<string,number>};

/** Presentation only: reads predicted poses and authoritative results, never changes combat. */
export class GameAudio {
  readonly mixer=new SoundMixer();
  private state:ArenaState|null=null;
  private tracks=new Map<string,Track>();
  private warned=new Set<string>();
  private zones=new Set<string>();
  private phase='';private count=-1;private active=false;private lastFrame=0;
  private materials=new Map<string,string>();
  constructor(arena:ArenaDefinition){
    this.setArena(arena);
    document.addEventListener('click',event=>{
      const target=event.target instanceof Element?event.target.closest('button'):null;
      if(target&&!target.closest('.audio-controls')&&!target.hasAttribute('data-button'))this.mixer.play('ui',undefined,{gain:.25,priority:0,gap:70});
    });
  }
  /** What the map's objects sound like when struck: wood, metal or stone (the map can change, D49). */
  setArena(arena:ArenaDefinition):void{
    this.materials.clear();
    for(const prop of arena.props??[])this.materials.set(prop.id,
      ['shack','cargo','crate','barrel','albero','wood'].includes(prop.kind)?'wood':
      ['braciere','anfora','metal'].includes(prop.kind)?'metal':'stone');
  }
  reset():void{this.mixer.stop();this.tracks.clear();this.warned.clear();this.zones.clear();this.state=null;this.phase='';this.count=-1;this.active=false;this.lastFrame=0;}
  update(state:ArenaState|null,local:PlayerState|null,localId:string|undefined,right:SoundPoint,training:boolean):void{
    if(state!==this.state){this.reset();this.state=state;}
    if(!state)return;
    const now=performance.now();
    const resync=document.hidden||now-this.lastFrame>500;
    if(resync){this.tracks.clear();this.warned.clear();this.zones.clear();}
    this.lastFrame=now;
    const focus=local??state.players.get(localId??'')??null;
    this.mixer.listener(focus,right);
    const phase=state.phase;
    const previousPhase=this.phase;
    const player=state.players.get(localId??'');
    const champion=player?.characterId??'';
    if(phase===MatchPhase.Countdown){const count=Math.ceil(state.timeLeft);if(count!==this.count){this.count=count;if(count>0)this.mixer.play('count',undefined,{gain:.65,priority:4,gap:100,rate:1+(3-count)*.12});}}
    else this.count=-1;
    if(phase===MatchPhase.Playing&&this.phase===MatchPhase.Countdown)this.mixer.play('go',undefined,{gain:.7,priority:4});
    this.phase=phase;
    const active=phase===MatchPhase.Playing||(phase===MatchPhase.Lobby&&training);
    if(!active){
      if(this.active)this.mixer.stop();
      if(phase===MatchPhase.Ended&&previousPhase===MatchPhase.Playing&&champion==='cleopatra'){
        if(state.winnerTeam||state.winner){
          const won=state.winnerTeam?player?.team===state.winnerTeam:state.winner===localId;
          void this.mixer.cleopatraLine(won?'win':'lose',3);
        }
      }
      this.tracks.clear();this.warned.clear();this.zones.clear();this.active=false;return;
    }
    this.active=true;
    const present=new Set<string>();
    for(const [id,player]of state.players){
      present.add(id);const pose=id===localId&&local?local:player;
      this.actor(id,pose,player.characterId,player.metamorphTicks,now);
    }
    // Decoys must sound like the original, otherwise footsteps reveal the real Gandhi.
    for(const [id,clone]of state.clones){
      const key='clone:'+id;present.add(key);
      this.actor(key,clone.actor,state.players.get(clone.owner)?.characterId??'gandhi',0,now);
    }
    for(const [id,creature]of state.creatures){
      const key='creature:'+id;present.add(key);
      const kind=creature.kind;if(!summonOf(kind))continue;
      // One arrival per summon: the three hoplites of a phalanx appear together.
      if(!resync&&!this.tracks.has(key)){const cue=creatureSound(kind);this.mixer.play(cue.group,creature.actor,{gain:.55,rate:cue.rate,key:`spawn:${creature.owner}:${kind}`,priority:2,gap:150});}
      this.actor(key,creature.actor,creature.actor.characterId,0,now,kind);
    }
    for(const id of this.tracks.keys())if(!present.has(id))this.tracks.delete(id);
    const tentacles=new Set<string>();
    for(const [id,t]of state.tentacles){
      tentacles.add(id);
      if(t.ticks<=LOVECRAFT_TENTACLE.warningTicks+LOVECRAFT_TENTACLE.tailTicks&&t.ticks>LOVECRAFT_TENTACLE.tailTicks&&!this.warned.has(id)){
        this.warned.add(id);
        if(!resync)this.mixer.play('warning',{x:t.x+t.fx*t.length/2,z:t.z+t.fz*t.length/2},{gain:.5,rate:.8,priority:4,key:'tentacle-warning-wave',gap:160});
      }
    }
    for(const id of this.warned)if(!tentacles.has(id))this.warned.delete(id);
    // A zone sounds where it appears; its caster's sound already played at the cast.
    for(const [id,zone]of state.zones){
      if(this.zones.has(id))continue;
      this.zones.add(id);const cue=zoneSound(zone.kind);
      if(cue&&!resync)this.mixer.play(cue.group,zone,{gain:.6,rate:cue.rate,priority:2,key:'zone:'+id});
    }
    for(const id of this.zones)if(!state.zones.has(id))this.zones.delete(id);
  }
  private actor(id:string,p:Pose,champion:string,metamorph:number,now:number,creature?:string):void{
    const previous=this.tracks.get(id);
    if(!previous){
      const seen=new Map<string,number>();
      // Joining, respawning or restoring a background tab must not replay an old attack.
      if(p.action!==Action.None)seen.set(`${p.actionSerial}:${p.action}`,now).set(`${p.actionSerial}:charge`,now);
      this.tracks.set(id,{x:p.x,y:p.y,z:p.z,grounded:p.grounded,dash:p.dashTicks,steps:0,seen});return;
    }
    const distance=Math.hypot(p.x-previous.x,p.z-previous.z);
    if(!p.knockedOut&&!p.matchFrozen){
      if(!creature&&previous.grounded&&!p.grounded&&p.vy>1)this.mixer.play('swing',p,{gain:.26,rate:1.3,key:id+':jump',gap:180});
      if(!creature&&!previous.grounded&&p.grounded&&Math.abs(p.y-previous.y)<3)this.mixer.play('step',p,{gain:.44,rate:.75,key:id+':land',gap:140});
      if(!creature&&p.dashTicks>0&&previous.dash===0)this.mixer.play('whip',p,{gain:.35,key:id+':dash',gap:180});
      // Maria Antonietta's whirlwind: a whoosh at every turn while it lasts.
      if(!creature&&(p.spinTicks??0)>0)this.mixer.play('swing',p,{gain:.32,rate:.75,key:id+':spin',gap:260});
      if(!creature&&p.grounded&&p.action===Action.None&&p.dashTicks===0&&distance<1.5){
        previous.steps+=distance;
        if(previous.steps>.95){previous.steps%=.95;this.mixer.play('step',p,{gain:.19,priority:0,key:id+':step',gap:170});}
      }else previous.steps=0;
      if(!creature&&p.action===Action.SuperSlam&&!previous.seen.has(`${p.actionSerial}:charge`)){
        previous.seen.set(`${p.actionSerial}:charge`,now);
        // The same rising cue for every champion, as long as the 0.4 s wind-up: always recognisable.
        if(p.actionTick<(attackDef(kitOf(champion,metamorph).table,Action.SuperSlam)?.startup??0))this.mixer.play('charge',p,{gain:.6,priority:3,key:id+':super'});
        // The air raid: an alarm across the whole arena, before the planes arrive.
        if(champion==='mussolini')this.mixer.play('siren',undefined,{gain:.5,priority:4,key:id+':siren'});
      }
      if(p.action!==Action.None){
        const moves=creature?summonOf(creature)!.kit.table:kitOf(champion,metamorph).table;
        const spec=attackDef(moves,p.action),key=`${p.actionSerial}:${p.action}`;
        if(spec&&p.actionTick>=spec.startup&&!previous.seen.has(key)){
          previous.seen.set(key,now);
          const cue=creature?creatureSound(creature):moveSound(champion,p.action,metamorph>0);
          this.mixer.play(cue.group,p,{gain:p.action===Action.SuperSlam?.8:.5,rate:cue.rate,priority:p.action>=Action.SuperSlam?2:1,key:id+':move',gap:65});
          if(!creature&&champion==='cleopatra'){
            if(p.action===Action.Special2)void this.mixer.cleopatraLine('bath');
            if(p.action===Action.SuperSlam)void this.mixer.cleopatraLine('super',2);
          }
          if(champion==='kafka'&&metamorph===0&&p.action>=Action.Light1&&p.action<=Action.Light3)this.mixer.play('bucket',p,{gain:.22,key:id+':bucket',gap:90});
        }
      }
    }
    for(const [key,time]of previous.seen)if(now-time>12000)previous.seen.delete(key);
    previous.x=p.x;previous.y=p.y;previous.z=p.z;previous.grounded=p.grounded;previous.dash=p.dashTicks;
  }
  combat(event:CombatEvent):void{
    const state=this.state;if(!state||!this.active||document.hidden)return;
    const player=(id:string)=>state.players.get(id);
    const play=(group:string,at?:SoundPoint,gain=.6,priority=2,key?:string)=>this.mixer.play(group,at,{gain,priority,key});
    switch(event.type){
      case 'hit':{
        const attacker=player(event.attacker),at=player(event.target)??state.creatures.get(event.target)?.actor;
        const champion=attacker?.characterId;
        const sound=champion==='kafka'?(attacker?.metamorphTicks?'heavy':'splash'):
          champion==='gandhi'&&event.action===Action.Light1?'wood':
          event.action===Action.Light3||event.action===Action.AirAttack?'heavy':'punch';
        play(sound,at,.7,2,'hit:'+event.target);
        // Bronze over the blow, his phalanx's included: a spearhead, or the deeper ring of a shield.
        if(champion==='auramennone')this.mixer.play('metal',at,{gain:.4,rate:event.action===Action.Light3||event.action===Action.DashAttack?.8:1.1,priority:2,key:'bronze:'+event.target});
        break;
      }
      case 'bomb':play('explosion',event,.9,3);break;
      // A brioche goes off: a softer bang, and the cream.
      case 'brioche':play('explosion',event,.5,3);play('splash',event,.45,2);break;
      case 'photo':play('shutter',event,.48,1);break;
      case 'tentacleHit':play('slime',event,.8,3,'tentacle-slam');play('heavy',event,.65,3,'tentacle-thud');break;
      case 'summonHit':{const creature=state.creatures.get(event.id);play(event.dead?'slime':'punch',creature?.actor,.5,2);break;}
      case 'ko':play('rumble',event,.65,3);break;
      case 'slamCancel':this.mixer.fade(event.caster+':super');this.mixer.fade(event.caster+':siren');play('cancel',player(event.caster),.55,3);break;
      case 'pickup':play('pickup',player(event.who),.4,2);break;
      case 'restore':play('pickup',player(event.caster),.3,2);break;
      case 'counter':case 'purify':play('magic',event,.55,2);break;
      case 'bounce':play('whip',event,.4,1);break;
      case 'slam':{
        const champion=player(event.caster)?.characterId;
        if(champion==='dante')play('fire',player(event.caster),.9,3);
        break;
      }
      case 'propHit':{const prop=state.props.get(event.id)??state.barricades.get(event.id);if(prop)play(state.barricades.has(event.id)?'wood':this.materials.get(event.id)??'stone',prop,.5,1,'prop:'+event.id);break;}
      case 'propBreak':play('break',event,.65,2);play(this.materials.get(event.id)??'stone',event,.5,1);break;
      case 'lever':play('metal',player(event.who),.55,2);break;
      // Action sounds come from observed active frames; move messages would duplicate prediction.
      default:break;
    }
  }
}
